/**
 * 오더목록 검색필터 공용 모듈 — $j.F.Order.SearchOpt
 *
 * 관리자 접수(/adm/orders)와 협력사 접수(/p/orders)가 같은 검색 규약을 쓴다.
 * 버튼(opt_*) 토글 → opt 문자열 → ajax 파라미터. 서버(OrderListService::buildSearchSql)가
 * 이 문자열을 strstr 로 파싱한다. 문자 매핑은 서버와 1:1 로 맞춰야 한다:
 *
 * 업종(c)은 셀렉트 값 그대로, 기종 상세검색(cr)은 오더 car_range 형식('334'·'100|16')이다
 * — 상세는 $j.F.Order.CarDetail 모달이 채운다.
 *
 *   배차유형  A 어플   B 지정   C 외부
 *   책임(결제) D 직수   E 싸인   F 카드
 *   수수료    H 0%     I 10%    J 15%   X 16+(보험 특례 14·16%)  Y 환원(수수료율에 안 맞아도
 *             환원완료 건은 포함하는 OR 예외)
 *   작업시간  L 일     M 오전   N 오후  O 시간  P 야간  Q 회차
 *   진행상태  R 대기   S 배차   T 작업중 U 작업완료  V 수금완료  W 취소
 *
 * 관리자의 '환원보류'는 이 opt 문자열에 없다 — 켠 것끼리 OR 로 넓히는 필터가 아니라 "보류된 것만"
 * 남기는 좁히기라, 수주대기·지정미확인처럼 상단 독립버튼(w=3)으로 뺐다.
 *
 * 상태(dateType/specialCond/sortField/sortOrder)는 호스트 페이지가 들고 있고,
 * 이 모듈은 DOM 을 읽고 쓰는 순수 함수만 제공한다.
 *
 * ▣ 발주자명/수주자명은 '이재일#2831' 규약을 쓴다
 *   인풋에 보이는 텍스트가 곧 검색기준이다. #번호 로 끝나면 그 회원 한 명(자동완성에서 고른 상태),
 *   아니면 이름 검색. 예전엔 번호를 hidden(wbn/wsn)+data-value 에 숨겨뒀는데, 표시값과 숨은 번호가
 *   어긋나도 화면에 드러나지 않아 "같은 화면에서 검색을 두번 누르면 결과가 다른" 원인이 됐다.
 *   숨은 상태를 없애면 그 어긋남 자체가 성립하지 않는다.
 */
import Util from '../../Common/Util';
export default class SearchOpt {

    /**
     * data-act 이름 → 호스트 페이지의 메서드명.
     *
     * 화면마다 없는 동작은 호스트에 메서드가 없으므로 그냥 무시된다
     * (협력사엔 환원예정·신규접수가 없고, 관리자엔 신규발주가 없다).
     */
    static ACTIONS = {
        'search':           'doSearch',
        'excel':            'saveToExcel',
        'date-type':        'toggleOdrDate',
        'find-user':        'showFindUser',
        'find-user-close':  'hideFindUser',
        'wait':             'checkWait',
        'jijung':           'jijungmi',
        'undone':           'checkUndone',
        'whan':             'checkWhan',
        'whan-done':        'checkWhanDone',
        'whan-hold':        'checkWhanHold',
        'preset-clear':     'exitPreset',   // 상태칩의 ✕ — 프리셋 해제(접수목록으로)
        'whan-sms':         'openWhanSms',
        'match-levels':     'openMatchLevels',  // 상세 펼침(카드)의 '단계분포' 버튼 — data-arg=오더번호
        'new-order':        'newOrder',
        'main':             'callMain',   // data-arg — 1 수금완료 / 4 푸시 / 6 문자
    };

    /**
     * 검색 필터 영역의 이벤트를 위임으로 바인딩한다.
     *
     * 블레이드에는 "무엇을 하는가"(data-act)만 남기고 "어떻게 부르는가"는 여기서 정한다.
     * 인라인 onclick 은 전역함수를 요구해서 페이지 클래스가 window.* 브리지를 잔뜩 달아야 했는데,
     * 위임으로 옮기면 그 브리지가 통째로 필요없어진다.
     *
     * opt 버튼(opt_A…)과 그룹 체크박스(tabChbox1…)는 id 규칙 자체가 이미 서버 opt 문자열과의
     * 계약이라 별도 속성 없이 id 로 위임한다.
     *
     * @param {object} host 페이지 인스턴스 ($j.p) — ACTIONS 의 메서드를 가진 객체
     * @param {Element|Document} [root] 위임 루트 (기본 document)
     */
    static bindFilters(host, root) {
        const $root = $(root || document);

        // 이름있는 동작
        $root.on('click', '[data-act]', function () {
            const method = SearchOpt.ACTIONS[this.dataset.act];
            if (!method || typeof host[method] !== 'function') return;
            const arg = this.dataset.arg;
            // ⚠️ preventDefault 를 걸지 않는다 — 신규접수 버튼처럼 자식에 인풋을 품은 요소가 있어
            //    기본동작을 막으면 그 인풋에 포커스가 가지 않는다 (인라인 onclick 시절과 동일하게 둔다)
            if (arg === undefined) host[method]();
            else host[method](arg !== '' && !isNaN(arg) ? Number(arg) : arg);
        });

        // opt 버튼 on/off + 전체선택/선택취소
        $root.on('click', '[id^="opt_"], #optall', function () { host.toggleButton(this); });

        // 그룹 단위 토글 — 제목 td 전체가 클릭영역이고, 안의 체크박스 id 가 그룹번호를 들고 있다
        $root.on('click', '.eatdTitle', function () {
            const n = parseInt(($(this).find('[id^="tabChbox"]').attr('id') || '').replace('tabChbox', ''), 10);
            if (n) host.toggleGroup(n);
        });

        // 인풋 — 엔터로 검색 / 클릭하면 비우기 / 값이 바뀌면 인풋 색상 갱신
        $root.on('keyup', '[data-enter-search]', function (e) { if (e.key === 'Enter') host.doSearch(); });
        $root.on('click', '[data-clear-on-click]', function () { host.clearInput(this); });
        $root.on('change', '[data-refresh-keys]', function () { host.checkSerKeys(); });

        // 엔터로 폼이 제출돼 페이지가 넘어가는 것 방지 (구 onsubmit="return false")
        $root.on('submit', '#inputform', (e) => e.preventDefault());
    }

    /** '이름#번호' 에서 회원번호만. 규약에 안 맞으면 0. */
    static memberNum(val) {
        const m = /#(\d+)\s*$/.exec(String(val || ''));
        return m ? parseInt(m[1], 10) : 0;
    }

    /** 이름+번호 → '이재일#2831' (번호가 없으면 이름만) */
    static memberLabel(name, num) {
        const n = parseInt(num, 10);
        return n > 0 ? `${String(name || '').trim()}#${n}` : String(name || '').trim();
    }

    /**
     * 회원 인풋(#wb/#ws)에 "번호로 확정됨" 표시를 갱신한다.
     * 테두리로만 구분한다 — 배경색은 checkSerKeys 가 "기본값과 다름" 표시에 이미 쓰고 있다.
     */
    static markMemberInputs() {
        ['wb', 'ws'].forEach((id) => {
            const el = document.getElementById(id);
            if (!el) return;
            el.classList.toggle('member-key-on', this.memberNum(el.value) > 0);
        });
    }

    /**
     * 서버 응답의 searchInfo → 목록 상단 안내 HTML.
     *
     * "이름검색은 몇 명이 걸렸는가" 를 보여주는 게 핵심이다. '김성' 두 글자면 소유자 189명이 잡히는데
     * 그 숫자가 안 보이면 결과가 왜 이렇게 넓은지 알 길이 없다. 숫자가 보이면 사용자가 스스로
     * 자동완성으로 좁힌다. 통합검색도 같은 이유로 "왜 0건인지" 를 돌려준다.
     */
    static memberSearchHtml(si) {
        if (!si) return '';

        const member = (label, s) => {
            if (!s || !s.mode) return '';
            if (s.mode === 'num') {
                return `<span class="member-search-tag is-num">${label}: ${this.escapeHtml(s.kwd)} <b>회원 1명</b></span>`;
            }
            const wide = s.count > 5 ? ' is-wide' : '';
            const cnt = s.count > 0 ? `<b>회원 ${Util.formatNumber(s.count)}명</b>` : '<b>일치 회원 없음</b>';
            return `<span class="member-search-tag is-name${wide}">${label}: '${this.escapeHtml(s.kwd)}' 이름검색 ${cnt}</span>`;
        };

        const text = (s) => {
            if (!s || !s.mode) return '';
            // 색인이 2글자 미만을 매칭 못해 무조건 0건이 되는 경우 — 결과가 아니라 입력이 문제다
            if (s.mode === 'short') {
                return `<span class="member-search-tag is-wide">통합검색: '${this.escapeHtml(s.kwd)}' <b>두 글자 이상 입력하세요</b></span>`;
            }
            return `<span class="member-search-tag is-name">통합검색: '${this.escapeHtml(s.kwd)}'</span>`;
        };

        return member('발주자', si.bal) + member('수주자', si.soo) + text(si.stx);
    }

    /** 안내문에 검색어를 그대로 싣기 위한 최소 이스케이프 */
    static escapeHtml(s) {
        return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    /**
     * 인풋에 회원을 꽂아넣는다 (테이블 셀 클릭 / 자동완성 선택 공용).
     * 검색은 실행하지 않는다 — 값만 들어가고 실행은 사용자가 검색 버튼으로 한다.
     */
    static setMemberInput(id, name, num) {
        const $el = $('#' + id);
        if (!$el.length) return;
        $el.val(this.memberLabel(name, num));
        this.markMemberInputs();
        // 상단 인풋이 조용히 바뀌면 "이 이름이 왜 여기 있지?" 가 되므로 한번 깜빡여 알린다
        $el.removeClass('member-flash');
        void $el[0].offsetWidth; // 연속 클릭 시 애니메이션 재시작
        $el.addClass('member-flash');
    }

    /**
     * opt_* 버튼 중 on 상태인 것의 suffix(A,B,C...)를 이어붙인 문자열.
     * 예: opt_A opt_D opt_R 이 on 이면 "ADR".
     */
    static make() {
        let ret = '';
        $('[id^="opt_"]').each(function () {
            if (($(this).attr('class') || '').indexOf('on') !== -1) {
                ret += this.id.replace('opt_', '');
            }
        });
        return ret;
    }

    /**
     * opt_* 버튼 on/off 토글.
     * id 가 'optall' 인 버튼만 특수 처리(rxon/rxoff 클래스 + 전체 토글) — 라벨은
     * toggleAll() 이 상태에 맞춰 '전체선택'/'선택취소' 로 같이 바꿔준다.
     * 그 외 그룹은 버튼 하나 토글 후 syncGroupCheckbox() 로 그 그룹의 헤더 체크박스를
     * 실제 상태(전부 on 이어야 체크)에 맞춘다 — 안 하면 개별 버튼을 하나 껐는데도 그룹 체크박스는
     * 계속 "전체선택"으로 남아 화면과 실제 필터가 어긋나 보인다.
     */
    static toggleButton(tar) {
        const id = tar.getAttribute('id');
        const isOn = ($('#' + id).attr('class') || '').indexOf('on') !== -1;
        if (id === 'optall') {
            this.toggleAll(!isOn);
        } else {
            $(tar).attr('class', isOn ? 'flink' : 'flink on');
            this.syncGroupCheckbox(tar);
        }
    }

    /**
     * 방금 토글된 버튼이 속한 그룹(#selgroup_{n})의 opt 버튼이 전부 on 인지 봐서
     * 그 그룹의 헤더 체크박스(#tabChbox{n})를 맞춘다 — 하나라도 꺼져 있으면 체크 해제.
     * @param {HTMLElement} tar 방금 toggleButton() 이 처리한 opt_* 버튼
     */
    static syncGroupCheckbox(tar) {
        const $group = $(tar).closest('[id^="selgroup_"]');
        if (!$group.length) return;
        const n = $group.attr('id').replace('selgroup_', '');
        const $opts = $group.find('[id^="opt_"]');
        const allOn = $opts.length > 0
            && $opts.toArray().every((el) => (el.className || '').indexOf('on') !== -1);
        $('#tabChbox' + n).prop('checked', allOn);
    }

    /**
     * 그룹 체크박스(tabChbox{n}) 상태로 해당 그룹의 opt 버튼 전체 on/off.
     * @param {number} n 그룹 인덱스 (1=배차유형, 2=책임, 3=수수료, 4=작업시간, 5=진행상태)
     */
    static toggleGroup(n) {
        const chk = $('#tabChbox' + n).is(':checked');
        const clss = chk ? ' on' : '';
        $('#selgroup_' + n).find('[id^="opt_"]').attr('class', 'flink' + clss);
    }

    /**
     * 모든 opt_* 버튼과 그룹 체크박스를 동일 상태로 세팅.
     * #optall 라벨도 같이 맞춘다 — 전체선택된 상태(on)면 다음 클릭은 해제이므로 '선택취소',
     * 아니면 다음 클릭은 전체선택이므로 '전체선택'. (checkWait/jijungmi 등 다른 진입점이
     * toggleAllOpt() 로 이걸 호출해도 #optall 라벨이 항상 실제 상태를 따라간다.)
     */
    static toggleAll(on) {
        const clss = on ? ' on' : '';
        $('[id^="opt_"]').attr('class', 'flink' + clss);
        $('[id^="tabChbox"]').prop('checked', !!on);
        $('#optall').attr('class', on ? 'flink rxon' : 'flink rxoff').text(on ? '선택취소' : '전체선택');
    }

    /**
     * 현재 검색 UI 상태를 서버 페이로드로 직렬화.
     * @param {object} st 호스트가 들고 있는 상태 { dateType, specialCond, sortField, sortOrder }
     */
    static params(st = {}) {
        return {
            d1: $('#d1').val(),
            d2: $('#d2').val(),
            dt: st.dateType,
            c:  $('#c').val(),
            cr: $('#cr').val(),   // 기종 상세검색 (없는 화면이면 빈값)
            opt: this.make(),
            x: $('#x').val(),
            y: $('#y').val(),
            z: $('#z').val(),
            stx: $('#stx').val(),
            n: $('#n').val(),
            // 번호검색이면 '이재일#2831' 형태로 값 안에 번호가 들어있다. 별도 hidden 을 두지 않으므로
            // 정렬·페이징처럼 doSearch 를 거치지 않는 reload 에서도 인풋과 검색기준이 어긋날 수 없다.
            wb: $('#wb').val(),
            ws: $('#ws').val(),
            w: st.specialCond,
            sst: st.sortField,
            sod: st.sortOrder,
        };
    }

    /**
     * 필터 인풋에 "기본값과 다름" 표시를 붙였다 뗀다 (`.is-changed`).
     *
     * ⚠ 예전엔 배경색을 **인라인으로** 칠했다(`style.backgroundColor = '#B5B5B5' | '#FFFFFF'`).
     *   인라인은 어떤 CSS 규칙도 이기므로, 다크에서 인풋이 **흰 바탕에 밝은 글자**가 되어
     *   글씨가 안 보였다(실측). 어떤 토큰화로도 못 잡는 자리라 클래스 토글로 바꿨다 —
     *   색은 sass pages/_order-list-legacy.scss 의 `.inftx` / `.inftx.is-changed` 가 정한다.
     *
     * @param {string[]} ids 검사할 인풋 id 목록 (화면마다 다름 — 협력사엔 x 셀렉트가 없다)
     */
    static checkSerKeys(defDayStart, defDayEnd, ids) {
        const mark = (el, changed) => { if (el) el.classList.toggle('is-changed', changed); };
        const d1 = document.getElementById('d1');
        const d2 = document.getElementById('d2');
        mark(d1, d1 && d1.value !== defDayStart);
        mark(d2, d2 && d2.value !== defDayEnd);
        (ids || ['c', 'x', 'y', 'z', 'stx', 'n', 'wb', 'ws']).forEach((id) => {
            const tar = document.getElementById(id);
            mark(tar, tar && tar.value !== '');
        });
    }

    /** 인풋 값 초기화 (클릭 시). 회원 인풋은 번호 표시도 함께 내려간다. */
    static clearInput(tar) {
        tar.value = '';
        this.markMemberInputs();
    }
}
